// Audio in the browser for searching by sound: a file or recording decoded to mono samples, the
// microphone recorded (with its level, for a meter), and samples written as a WAV file (to keep a
// recording with the rhythm it became).
export const MAX_RECORDING_SECONDS = 20;

// Mono samples and their rate from an audio file or blob (anything the browser can play).
export async function decodeAudio(blob) {
  const Context = window.AudioContext || window.webkitAudioContext, context = new Context();
  try {
    const buffer = await context.decodeAudioData(await blob.arrayBuffer());
    const samples = new Float32Array(buffer.length);
    for (let channel = 0; channel < buffer.numberOfChannels; channel++) { const data = buffer.getChannelData(channel); for (let i = 0; i < data.length; i++) samples[i] += data[i] / buffer.numberOfChannels; }
    return { samples, rate: buffer.sampleRate, seconds: buffer.duration };
  } catch { throw new Error("That file couldn't be read as audio. Try an MP3, WAV or OGG."); }
  finally { context.close?.(); }
}

// Records the microphone until stop() (or MAX_RECORDING_SECONDS): resolves to { samples, rate }.
// onLevel(0–1) reports loudness many times a second.
export async function recordMicrophone({ onLevel = () => {}, onStop = () => {} } = {}) {
  if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") throw new Error("This browser can't record. Choose a file instead.");
  let stream;
  try { stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false } }); }
  catch { throw new Error("The microphone isn't available. Allow it for TrueChops, or choose a file instead."); }
  const Context = window.AudioContext || window.webkitAudioContext, context = new Context(), analyser = context.createAnalyser();
  context.createMediaStreamSource(stream).connect(analyser);
  const data = new Float32Array(analyser.fftSize), chunks = [], recorder = new MediaRecorder(stream);
  let frame = 0;
  const meter = () => { analyser.getFloatTimeDomainData(data); let peak = 0; for (const value of data) peak = Math.max(peak, Math.abs(value)); onLevel(Math.min(1, peak)); frame = requestAnimationFrame(meter); };
  meter();
  recorder.ondataavailable = (event) => { if (event.data.size) chunks.push(event.data); };
  const finished = new Promise((resolve) => { recorder.onstop = resolve; });
  recorder.start();
  const limit = setTimeout(() => stop(), MAX_RECORDING_SECONDS * 1000);
  async function stop() {
    clearTimeout(limit); cancelAnimationFrame(frame);
    if (recorder.state !== "inactive") recorder.stop();
    await finished;
    stream.getTracks().forEach((track) => track.stop()); context.close?.();
    onStop();
    return decodeAudio(new Blob(chunks, { type: recorder.mimeType || "audio/webm" }));
  }
  return { stop };
}

// Samples as a 16-bit mono WAV file.
export function wavFile(samples, rate, name = "recording.wav") {
  const buffer = new ArrayBuffer(44 + samples.length * 2), view = new DataView(buffer);
  const text = (at, value) => { for (let i = 0; i < value.length; i++) view.setUint8(at + i, value.charCodeAt(i)); };
  text(0, "RIFF"); view.setUint32(4, 36 + samples.length * 2, true); text(8, "WAVE"); text(12, "fmt ");
  view.setUint32(16, 16, true); view.setUint16(20, 1, true); view.setUint16(22, 1, true); view.setUint32(24, rate, true); view.setUint32(28, rate * 2, true); view.setUint16(32, 2, true); view.setUint16(34, 16, true);
  text(36, "data"); view.setUint32(40, samples.length * 2, true);
  for (let i = 0; i < samples.length; i++) view.setInt16(44 + i * 2, Math.max(-1, Math.min(1, samples[i])) * 0x7fff, true);
  return new File([buffer], name, { type: "audio/wav" });
}
